
import { getEpochSecondsFromDay } from "@/helpers/time";
import { isValidDashboardUid } from "@/helpers/helpers";
import { defineComponent, toRefs, reactive } from "vue";
import { useRoute, useRouter } from "vue-router";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import { Temporal } from "@js-temporal/polyfill";
import VueFeather from "vue-feather";

interface HTMLiframeObject extends HTMLElement {
  contentDocument: Document; // Returns the document object generated by an iframe
  contentWindow: Window; // Returns the window object generated by an iframe
  height: string; // Sets or returns the value of the height attribute in an iframe
  name: string; // Sets or returns the value of the name attribute in an iframe
  sandbox: string; // Returns the value of the sandbox attribute in an iframe
  seamless: boolean; // Sets or returns whether an iframe should look like it is a part of the containing document (no borders or scrollbars), or not
  src: string; // Sets or returns the value of the src attribute in an iframe
  srcdoc: string; // Sets or returns the value of the srcdoc attribute in an iframe
  width: string; // Sets or returns the value of the width attribute in an iframe
}

export default defineComponent({
  name: "GraphsPage",
  components: {
    LoadingSpinner,
    VueFeather,
  },
  setup() {
    const state = reactive({
      baseURL: window.location.origin,
      iframeLoaded: false,
      errorMessage: "",
    });

    const router = useRouter();
    const route = useRoute();

    // Deep link support: /graphs?dashboard=<uid>&from=<time>&to=<time> opens a specific dashboard.
    // Params are validated before being interpolated into the iframe src.
    const isValidGrafanaTime = (value: unknown): value is string =>
      typeof value === "string" && (/^\d{1,17}$/.test(value) || /^now([+-]\d+[smhdwMy])?$/.test(value));

    const dashboardVariableParams: [string, string, RegExp][] = [
      ["asset", "asset", /^[\w .:-]{1,64}$/],
      ["node_location", "node_location", /^\d$/],
      ["waterline_pct", "waterline_pct", /^\d{1,2}(\.\d{1,2})?$/],
    ];

    let iframeSrc = "/grafana/dashboards?orgId=1&kiosk";
    const dashboardUid = route.query.dashboard;
    if (typeof dashboardUid === "string" && dashboardUid !== "") {
      if (isValidDashboardUid(dashboardUid)) {
        iframeSrc = `/grafana/d/${dashboardUid}?orgId=1&kiosk`;
        if (isValidGrafanaTime(route.query.from)) iframeSrc += `&from=${route.query.from}`;
        if (isValidGrafanaTime(route.query.to)) iframeSrc += `&to=${route.query.to}`;
        // Dashboard variable pre-selection (diagnosis links): allowlisted names, validated values
        for (const [param, variable, pattern] of dashboardVariableParams) {
          const value = route.query[param];
          if (typeof value === "string" && pattern.test(value)) {
            iframeSrc += `&var-${variable}=${encodeURIComponent(value)}`;
          }
        }
      } else {
        state.errorMessage = "Invalid dashboard link, showing the dashboard list instead";
      }
    }

    // Hack the CSS for the Grafana iframe to hide bits we don't want the user to see.
    const styleiframe = () => {
      if (document.getElementById("iframe1")) {
        const iframe = document.getElementById("iframe1") as HTMLiframeObject;
        if (iframe) {
          const myiFrame = iframe.contentDocument;
          const styles = document.createElement("style");
          const styleContent = document.createTextNode(
            ".toolbar-button[aria-label='Save dashboard'], .toolbar-button[aria-label='Add panel'], .toolbar-button[aria-label='Dashboard settings'], .css-15okvyg, .css-1opalb8, .gf-form-label--dashlink, .css-zanmp0, .sidemenu, .footer, .page-header__inner, .css-1hgwamg-page-header, .page-header__tabs, .page-action-bar{display: none !important;} .page-content, .css-1736fpx-page-content, .page-container {margin: 0 !important; border: 0 !important;} .css-60onds{padding-top: 40px;}"
          );
          styles.appendChild(styleContent);
          myiFrame.head.appendChild(styles);
        }
      }
      state.iframeLoaded = true;
    };

    const getTimePeriod = () => {
      const iframe = document.getElementById("iframe1") as HTMLiframeObject;
      if (iframe) {
        const iframeURLString = iframe.contentDocument.location.href;
        const urlParams = new URLSearchParams(iframeURLString);
        const [timeFrom, timeTo] = [urlParams.get("from"), urlParams.get("to")];
        if (!timeFrom || !timeTo) {
          state.errorMessage = "Please select a time period from a dashboard below";
          return;
        }
        if (timeTo == "now") {
          state.errorMessage = "Please select a non relative time period from a dashboard below";
          return;
        }
        const routeData = {
          name: "Bursts",
          query: {
            startDate: timeFrom.toString(),
            endDate: timeTo.toString(),
            dateSet: 1,
          },
        };
        const { href } = router.resolve(routeData);
        window.open(href, "_blank");
      }
    };

    const reloadIframe = () => {
      state.iframeLoaded = false;
      const iframe = document.getElementById("iframe1") as HTMLiframeObject;
      if (iframe) {
        const newSrc = iframe.src;
        iframe.src = newSrc;
      }
    };

    return {
      ...toRefs(state),
      iframeSrc,
      getEpochSecondsFromDay,
      styleiframe,
      getTimePeriod,
      reloadIframe,
    };
  },
});
